import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronDown, ChevronUp, Eye, Scale } from "lucide-react";
import { PatternAnswerItem } from "@/components/app/PatternAnswerReview";

export function outcomeLine(state) {
  const prefix = state.source === "realisation" || state.source === "pattern_prompt"
    ? "You've already answered this on a similar question: " + state.label + "."
    : state.inherited
      ? "You answered this earlier: " + state.label + "."
      : "You said: " + state.label + ".";
  if (state.outcome_type === "realisation") {
    const how = state.outcome === "confirmed" ? "now part of what WUA has learned about you" : "noted as a current leaning";
    return prefix + " It's " + how + " — see My Realisations.";
  }
  if (state.outcome_type === "tradeoff") {
    return state.outcome === "rejected"
      ? prefix + " Saved as a concern — the affected choices are marked for another look on your shortlist."
      : prefix + " Saved as a trade-off you're willing to make.";
  }
  if (state.outcome_type === "conflict") {
    return prefix + " The affected choices are marked “Worth another look” on your shortlist.";
  }
  if (state.outcome_type === "tension") {
    return state.outcome === "tension_acknowledged"
      ? prefix + " Noted that you recognise this tension — nothing has been decided and your shortlist is unchanged."
      : prefix + " Noted as a possible tension — nothing has been decided and your shortlist is unchanged.";
  }
  if (state.outcome_type === "provisional") {
    return prefix + " Noted as a leaning — no decision has been recorded yet.";
  }
  return prefix + " You can change your mind any time — earlier answers stay in your history.";
}

function WorthItFollowUp({ obs, worthIt, onSaved }) {
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState("");
  const current = worthIt.current?.code;

  const choose = async (code) => {
    if (saving || code === current) return;
    setSaving(code);
    setError("");
    try {
      const { data } = await api.post("/realisations/observations/" + obs.observation_id + "/worth-it", { choice: code });
      onSaved(data?.observation);
    } catch {
      setError("We couldn't save that just now. Refresh and try again.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="mt-3 border-t border-stone-200 pt-3" data-testid={"observation-worth-it-" + obs.observation_id}>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-stone-800">
        <Scale className="h-3.5 w-3.5 text-[#4C1D95]" />
        {worthIt.question}
        <span className="font-normal text-stone-400">· optional</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {worthIt.options.map((opt) => (
          <button
            key={opt.code}
            type="button"
            disabled={Boolean(saving)}
            onClick={() => choose(opt.code)}
            data-testid={"observation-worth-it-" + opt.code + "-" + obs.observation_id}
            className={
              "rounded-full border px-2.5 py-1 text-[11px] transition-colors disabled:opacity-60 " +
              (current === opt.code
                ? "border-[#4C1D95] bg-violet-50 font-semibold text-[#4C1D95]"
                : "border-stone-300 bg-white text-stone-600 hover:border-[#4C1D95] hover:text-[#4C1D95]")
            }
          >
            {saving === opt.code ? "Saving…" : opt.label}
          </button>
        ))}
      </div>
      {worthIt.current && (
        <div className="mt-1.5 text-[11px] text-stone-500" data-testid={"observation-worth-it-current-" + obs.observation_id}>
          {worthIt.current.relation === "outweighs"
            ? "Noted: " + worthIt.current.label.toLowerCase() + " is what makes this worth it. You can change this any time."
            : "Noted — you're not sure yet. You can come back to this any time."}
        </div>
      )}
      {error && <div className="mt-1.5 text-[11px] text-rose-700">{error}</div>}
    </div>
  );
}

export function ObservationReactions({ obs, onReacted, hideState = false }) {
  const [state, setState] = useState(obs.response_state);
  const [worthIt, setWorthIt] = useState(obs.worth_it);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState("");
  const [failed, setFailed] = useState(null);

  useEffect(() => {
    setState(obs.response_state);
    setWorthIt(obs.worth_it);
  }, [obs]);

  const respond = async (code) => {
    if (saving) return;
    setSaving(code);
    setError("");
    try {
      const { data } = await api.post(
        "/realisations/observations/" + obs.observation_id + "/respond",
        { response: code }
      );
      setState(data?.observation?.response_state || null);
      setWorthIt(data?.observation?.worth_it || null);
      setFailed(null);
      onReacted?.(data);
    } catch {
      setFailed(code);
      setError("We couldn't save that just now — nothing has been changed.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div data-testid={"observation-reactions-" + obs.observation_id}>
      {state?.revisit && (
        <div className="mt-3 border-l-2 border-amber-300 pl-3 text-sm text-stone-700" data-testid={"observation-revisit-" + obs.observation_id}>
          <p>{state.revisit}</p>
          <p className="mt-1 text-stone-500">Your earlier answer was: {state.label}. Keep it, or choose a different one.</p>
          <button type="button" disabled={Boolean(saving)} onClick={() => respond(state.response)}
            data-testid={"observation-keep-" + obs.observation_id}
            className="mt-2 min-h-9 rounded-full border border-[#4C1D95] px-3 py-1.5 text-xs font-semibold text-[#4C1D95] disabled:opacity-60">
            {saving === state.response ? "Saving…" : "Keep my answer"}
          </button>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {obs.options.map((opt) => {
          const chosen = state?.response === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              disabled={Boolean(saving)}
              onClick={() => respond(opt.code)}
              data-testid={"observation-respond-" + opt.code + "-" + obs.observation_id}
              className={
                "min-h-9 rounded-full border px-3 py-1.5 text-left text-xs font-medium transition-colors disabled:opacity-60 " +
                (chosen
                  ? "border-[#4C1D95] bg-violet-50 text-[#4C1D95]"
                  : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]")
              }
            >
              {saving === opt.code ? "Saving…" : opt.label}
            </button>
          );
        })}
      </div>
      {state && !hideState && !state.revisit && (
        <div className="mt-2 text-[11px] text-stone-500" data-testid={"observation-state-" + obs.observation_id}>
          {outcomeLine(state)}
        </div>
      )}
      {worthIt && (
        <WorthItFollowUp
          obs={obs}
          worthIt={worthIt}
          onSaved={(next) => {
            setWorthIt(next?.worth_it || null);
            onReacted?.({ observation: next });
          }}
        />
      )}
      {error && (
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-rose-700" role="alert" data-testid={"observation-error-" + obs.observation_id}>
          {error}
          {failed && (
            <button type="button" onClick={() => respond(failed)} data-testid={"observation-retry-" + obs.observation_id}
              className="font-semibold underline underline-offset-2">Try again</button>
          )}
        </div>
      )}
    </div>
  );
}

export function ObservationCard({ obs, onReacted }) {
  return (
    <div
      className={
        "min-w-0 break-words [overflow-wrap:anywhere] rounded-md border bg-white p-4 " +
        (obs.tension ? "border-amber-300" : "border-stone-200")
      }
      data-testid={"observation-card-" + obs.observation_id}
    >
      <div className="text-sm font-semibold text-stone-900">{obs.headline}</div>
      {obs.source_cue && (
        <div
          className={"mt-1 text-[11px] " + (obs.tension || obs.mixed_evidence ? "text-amber-800" : "text-stone-500")}
          data-testid={"observation-source-cue-" + obs.observation_id}
        >
          {obs.source_cue}
        </div>
      )}
      <div className="mt-3 text-[10px] font-underground-sc tracking-[0.14em] text-stone-500">
        What I can see
      </div>
      <p className="mt-1 text-sm text-stone-700" data-testid={"observation-fact-" + obs.observation_id}>
        {obs.observation_text}
      </p>
      {obs.evidence_summary?.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[11px] text-stone-500">
          {obs.evidence_summary.map((label, i) => (
            <li key={i}>· {label}</li>
          ))}
        </ul>
      )}
      <div className="mt-3 text-[10px] font-underground-sc tracking-[0.14em] text-[#4C1D95]">
        What that might mean
      </div>
      <p className="mt-1 text-sm italic text-stone-700" data-testid={"observation-interpretation-" + obs.observation_id}>
        {obs.interpretation_text}
      </p>
      <ObservationReactions obs={obs} onReacted={onReacted} />
    </div>
  );
}

export function isOpenQuestion(o) {
  return !o.response_state || Boolean(o.response_state.revisit);
}

function ackText(state) {
  if (!state) return "Your answer is saved.";
  if (state.outcome_type === "realisation") {
    return state.outcome === "confirmed"
      ? "Saved — this is now in My Realisations."
      : "Noted as a leaning, not a confirmation. You'll find it in My Realisations as a leaning.";
  }
  if (state.response === "not_sure") return "Noted — you're not sure yet. We won't ask again unless something important changes.";
  return "Your answer is saved. " + outcomeLine(state);
}

function PreviousAnswers({ list, patterns = [], onReacted, onPatternChanged }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  if (!list.length && !patterns.length) return null;
  return (
    <div className="mt-3" data-testid="observations-previous">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="observations-previous-toggle"
        className="inline-flex min-h-10 items-center gap-1 text-sm font-medium text-stone-600 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]">
        Your previous answers · {list.length + patterns.length}
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <ul className="mt-2 divide-y divide-stone-200 border-y border-stone-200" data-testid="observations-previous-list">
          {list.map((o) => (
            <li key={o.observation_id} className="py-3" data-testid={"observation-previous-" + o.observation_id}>
              <div className="text-sm font-semibold text-stone-900">{o.headline}</div>
              <div className="mt-1 text-xs text-stone-600" data-testid={"observation-state-" + o.observation_id}>{outcomeLine(o.response_state)}</div>
              <button type="button" onClick={() => setEditing(editing === o.observation_id ? null : o.observation_id)}
                aria-expanded={editing === o.observation_id} data-testid={"observation-change-" + o.observation_id}
                className="mt-1 min-h-9 text-xs font-semibold text-[#4C1D95] underline-offset-2 hover:underline">
                {editing === o.observation_id ? "Keep this answer" : "Change my answer"}
              </button>
              {editing === o.observation_id && (
                <ObservationReactions obs={o} hideState onReacted={(d) => { setEditing(null); onReacted(o, d); }} />
              )}
            </li>
          ))}
          {patterns.map((p) => <PatternAnswerItem key={p.candidate_id} item={p} onChanged={onPatternChanged} />)}
        </ul>
      )}
      <Link to="/realisations" className="ml-0 mt-1 inline-flex min-h-10 items-center text-sm font-medium text-[#4C1D95] underline-offset-2 hover:underline sm:ml-4"
        data-testid="observations-my-realisations-link">See My Realisations</Link>
    </div>
  );
}

// Shortlist mode: at most one open question; answered ones fold into a closed review list (nothing is deleted).
function FocusedObservations({ observations, setObservations, patterns, setPatterns, onReacted, suppress, title, id }) {
  const [ack, setAck] = useState("");
  const [answeredNow, setAnsweredNow] = useState([]);
  const ackRef = useRef(null);
  const open = observations.filter(isOpenQuestion);
  const current = suppress || answeredNow.length ? null : open[0];  // one question per visit; never chain the next one
  const answered = observations.filter((o) => !isOpenQuestion(o));
  const concepts = new Set(observations.map((o) => o.concept_ref).filter(Boolean));
  const answeredPatterns = patterns.filter((p) => !concepts.has(p.concept_ref));  // same concept is reviewed once

  const answeredOne = (o, data) => {
    const next = data?.observation;
    if (next) setObservations((list) => list.map((x) => (x.observation_id === o.observation_id ? { ...x, ...next } : x)));
    setAnsweredNow((ids) => [...ids, o.observation_id]);
    setAck(ackText(next?.response_state));
    requestAnimationFrame(() => ackRef.current?.focus());  // keep focus in place when the card folds away
    onReacted?.(data);
  };
  const patternChanged = (item, message) => {
    setPatterns((list) => list.map((p) => (p.candidate_id === item.candidate_id ? item : p)));
    setAck(message);
    requestAnimationFrame(() => ackRef.current?.focus());
    onReacted?.({ pattern: item });
  };
  if (!current && !answered.length && !answeredPatterns.length) return null;
  return (
    <section id={id} className="mb-6" data-testid="realisation-observations">
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4 text-[#4C1D95]" />
        <div className="text-[10px] font-underground-sc tracking-[0.14em] text-[#4C1D95]">{title}</div>
      </div>
      <p ref={ackRef} tabIndex={-1} role="status" aria-live="polite" data-testid="observations-ack"
        className={"text-sm text-emerald-800 focus:outline-none " + (ack ? "mt-2 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-300" : "sr-only")}>{ack}</p>
      {current && (
        <div className="mt-3 max-w-2xl" data-testid="observations-active">
          <ObservationCard key={current.observation_id} obs={current} onReacted={(d) => answeredOne(current, d)} />
        </div>
      )}
      <PreviousAnswers list={answered} patterns={answeredPatterns} onReacted={answeredOne} onPatternChanged={patternChanged} />
    </section>
  );
}

export default function RealisationObservations({ user, shortlistCount, onReacted, refreshKey = 0, title = "What I'm noticing", intro, id, focus = false, suppressActive = false, onLoaded }) {
  const [observations, setObservations] = useState([]);
  const [patterns, setPatterns] = useState([]);

  useEffect(() => {
    if (!user || !focus) {
      setPatterns([]);
      return;
    }
    let cancelled = false;
    api.get("/realisations/patterns/answered")
      .then(({ data }) => { if (!cancelled) setPatterns(data?.patterns || []); })
      .catch(() => { if (!cancelled) setPatterns([]); });
    return () => { cancelled = true; };
  }, [user, focus, shortlistCount, refreshKey]);

  useEffect(() => {
    if (!user) {
      setObservations([]);
      return;
    }
    let cancelled = false;
    api
      .get("/realisations/observations/current")
      .then(({ data }) => {
        if (!cancelled) {
          setObservations(data?.observations || []);
          onLoaded?.(data?.observations || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setObservations([]);
          onLoaded?.([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user, shortlistCount, refreshKey]);

  if (focus) {
    return <FocusedObservations observations={observations} setObservations={setObservations} patterns={patterns}
      setPatterns={setPatterns} onReacted={onReacted}
      suppress={suppressActive} title={title} id={id} />;
  }
  if (!observations.length) return null;

  return (
    <section
      id={id}
      className="mb-6 rounded-md border border-violet-200 bg-violet-50/30 p-4 sm:p-5"
      data-testid="realisation-observations"
    >
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4 text-[#4C1D95]" />
        <div className="text-[10px] font-underground-sc tracking-[0.14em] text-[#4C1D95]">
          {title}
        </div>
      </div>
      <p className="mt-1 text-xs text-stone-600">
        {intro || "A few patterns in the choices you've made so far. They're observations, not conclusions — tell me if they fit."}
      </p>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {observations.map((obs) => (
          <ObservationCard key={obs.observation_id + ":" + refreshKey} obs={obs} onReacted={onReacted} />
        ))}
      </div>
    </section>
  );
}
